import React from 'react'
import { useAuth } from '../contexts/AuthContext'
import { IndustryProvider } from '../contexts/IndustryContext'
import { CustomerProvider } from '../contexts/CustomerContext'

/**
 * Wrapper component that connects AuthContext to IndustryProvider.
 * This ensures that when a user logs in with an organization, their
 * organization's industry is used for the IndustryContext.
 *
 * Implements synchronization to prevent race conditions between auth
 * and industry context initialization.
 */
export default function AuthAwareIndustryProvider({ children }: { children: React.ReactNode }) {
  const { industry, isLoading: authLoading } = useAuth()
  const [syncedIndustry, setSyncedIndustry] = React.useState<string | null>(null)

  // Track when industry from auth has been synced to state
  React.useEffect(() => {
    if (!authLoading) {
      setSyncedIndustry(industry)
    }
  }, [authLoading, industry])

  // Only render children after auth completes and industry is synced
  // This prevents components from rendering with stale industry data
  const shouldRender = !authLoading && (syncedIndustry === industry)

  return (
    <IndustryProvider organizationIndustry={industry}>
      <CustomerProvider>
        {shouldRender ? children : (
          <div className="min-h-screen bg-surface-900 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-surface-400">Loading...</p>
            </div>
          </div>
        )}
      </CustomerProvider>
    </IndustryProvider>
  )
}
